/**
 * PlayerSummaryCardContainer - Container for AI-generated player summary card
 *
 * Manages AI summary generation using SSE, language selection, caching,
 * and modal state for full summary view.
 * @remarks This is a Container component - handles all business logic
 */

import { PlayerDetailPlayerMetaFragment } from "@/generated/graphql";

import { useAISummaryCard } from "@/features/player-detail/hooks/useAISummaryCard";
import PlayerSummaryCardView from "./PlayerSummaryCardView";

/**
 * Props for PlayerSummaryCardContainer component
 */
interface PlayerSummaryCardContainerProps {
  /** Player data fragment */
  player: PlayerDetailPlayerMetaFragment | null | undefined;
  /** Whether dark mode is active */
  isDark: boolean;
}

/**
 * PlayerSummaryCardContainer handles AI summary generation and state
 * @param props - Container props
 * @param props.player - Player data fragment
 * @param props.isDark - Whether dark mode is active
 * @returns PlayerSummaryCardView with computed props
 */
const PlayerSummaryCardContainer = ({
  player,
  isDark: externalIsDark,
}: PlayerSummaryCardContainerProps) => {
  const {
    isDark,
    isModalOpen,
    displayContent,
    fullContent,
    isGenerating,
    error,
    lastUpdated,
    showReadMore,
    pickerLocale,
    saveAsDefault,
    selectedLanguageLabel,
    supportedLanguages,
    handleReadMore,
    handleModalClose,
    handleCopySummary,
    onSelectLanguage,
    isButtonDisabled,
  } = useAISummaryCard({ player, externalIsDark });

  return (
    <PlayerSummaryCardView
      isDark={isDark}
      summaryContent={displayContent}
      fullSummaryContent={fullContent}
      lastUpdated={lastUpdated}
      isGenerating={isGenerating}
      hasError={!!error}
      errorMessage={error}
      showReadMore={showReadMore}
      isModalOpen={isModalOpen}
      selectedLanguageLabel={selectedLanguageLabel}
      supportedLanguages={[...supportedLanguages]}
      selectedLanguage={pickerLocale}
      saveAsDefault={saveAsDefault}
      onReadMore={handleReadMore}
      onModalClose={handleModalClose}
      onSelectLanguage={onSelectLanguage}
      isButtonDisabled={isButtonDisabled}
      onCopySummary={handleCopySummary}
    />
  );
};

PlayerSummaryCardContainer.displayName = "PlayerSummaryCardContainer";

export default PlayerSummaryCardContainer;
